/**
 * Home Redesign — Sections
 * ─────────────────────────────────────────────────────────────────────────
 * Implements the homepage blueprint from `/app/design_guidelines.md`:
 *
 *   • WalletHero            — Apple Wallet-style card + rotating featured photo
 *   • EditorialGallery      — bento collage grid using the 10 new photos
 *   • PromoterTeaser        — recruitment CTA → /promoters
 *   • ReservationStripFooter — bottom conversion strip + utility links
 *
 * All existing copy strings are preserved verbatim per the brief.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Button } from '../ui/button';
import { ArrowRight, BadgeIndianRupee, Calendar as CalendarIcon, LogIn, MapPin, Sparkles, ScanLine } from 'lucide-react';

// ─── Local image manifest (10 nightlife photos) ─────────────────────────────
const PHOTOS = [
  '/images/home/scratch-night-01.jpg',
  '/images/home/scratch-night-02.jpg',
  '/images/home/scratch-night-03.jpg',
  '/images/home/scratch-night-04.jpg',
  '/images/home/scratch-night-05.jpg',
  '/images/home/scratch-night-06.jpg',
  '/images/home/scratch-night-07.jpg',
  '/images/home/scratch-night-08.jpg',
  '/images/home/scratch-night-09.jpg',
  '/images/home/scratch-night-10.jpg',
];

// Hero rotation pool — the most "hero-friendly" looking photos go first.
const HERO_ROTATION = [PHOTOS[0], PHOTOS[4], PHOTOS[5], PHOTOS[6]];

// ─── Reusable membership-mark pill (per blueprint §7) ───────────────────────
function MembershipPill({ children, testId, dotted = true, tone = 'gold' }) {
  const base =
    tone === 'gold'
      ? 'border-[rgba(212,168,67,0.35)] bg-black/40 text-[#F1E2B8]'
      : 'border-white/15 bg-white/[0.02] text-white/70';
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold tracking-[0.22em] uppercase backdrop-blur-md ${base}`}
    >
      {dotted && (
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#D4A843] shadow-[0_0_0_3px_rgba(212,168,67,0.18)]" />
      )}
      {children}
    </span>
  );
}

// ─── Scroll-reveal utility (IntersectionObserver, prefers-reduced-motion-aware)
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      el.classList.add('opacity-100', 'translate-y-0');
      return;
    }
    el.classList.add('opacity-0', 'translate-y-2', 'transition-all', 'duration-[550ms]');
    el.style.transitionTimingFunction = 'cubic-bezier(0.22,1,0.36,1)';
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.remove('opacity-0', 'translate-y-2');
            e.target.classList.add('opacity-100', 'translate-y-0');
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

// ════════════════════════════════════════════════════════════════════════════
//  1. WALLET HERO
// ════════════════════════════════════════════════════════════════════════════
export function WalletHero({ navigate }) {
  const [idx, setIdx] = useState(0);
  const reduceMotion = useRef(false);

  useEffect(() => {
    reduceMotion.current =
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false;
    if (reduceMotion.current) return;
    const id = setInterval(() => {
      setIdx((i) => (i + 1) % HERO_ROTATION.length);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      data-testid="home-hero"
      className="relative overflow-hidden bg-[#0A0A0A]"
    >
      {/* Layered ambient backdrop */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(900px circle at 18% 12%, rgba(212,168,67,0.14), transparent 55%),' +
            'radial-gradient(900px circle at 82% 0%, rgba(212,168,67,0.06), transparent 55%),' +
            'linear-gradient(180deg, #0A0A0A 0%, #0B0B0D 100%)',
        }}
      />

      <div className="relative mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-10 pt-6 sm:pt-10 lg:pt-14 pb-10 lg:pb-16 grid gap-6 lg:gap-10 lg:grid-cols-[1.05fr_0.95fr]">

        {/* ── Mobile-first: featured photo first ─────────────────────────── */}
        <div className="order-1 lg:order-2 relative">
          <div className="relative">
            {/* Two offset 'print' frames behind for collage depth (desktop only) */}
            <div
              aria-hidden="true"
              className="hidden lg:block absolute -top-3 -left-3 right-6 bottom-6 rounded-[24px] border border-white/10 bg-[#101013] rotate-[-1.6deg] shadow-[0_18px_50px_rgba(0,0,0,0.5)]"
            />
            <div
              aria-hidden="true"
              className="hidden lg:block absolute -bottom-4 -right-3 left-6 top-6 rounded-[24px] border border-white/10 bg-[#0E0E12] rotate-[1.4deg] shadow-[0_18px_50px_rgba(0,0,0,0.5)]"
            />

            {/* Active featured frame */}
            <div className="relative overflow-hidden rounded-[24px] border border-white/10 bg-[#121216] shadow-[0_18px_50px_rgba(0,0,0,0.65)] aspect-[16/11] sm:aspect-[4/5] lg:aspect-[16/11]">
              {HERO_ROTATION.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt={`Scratch Unleashed nightlife photo ${i + 1}`}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  decoding={i === 0 ? 'sync' : 'async'}
                  fetchpriority={i === 0 ? 'high' : 'low'}
                  className="absolute inset-0 h-full w-full object-cover will-change-[opacity] transition-opacity duration-[550ms]"
                  style={{
                    opacity: idx === i ? 1 : 0,
                    transitionTimingFunction: 'cubic-bezier(0.22,1,0.36,1)',
                  }}
                  data-testid={i === idx ? 'home-hero-active-photo' : undefined}
                />
              ))}
              {/* Gradient overlay for label readability */}
              <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/10 via-black/10 to-black/80"
              />
              {/* Corner label */}
              <div className="absolute top-3 right-3">
                <MembershipPill testId="home-hero-live-status-pill">
                  TONIGHT · 10PM
                </MembershipPill>
              </div>
              {/* Pagination dots */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                {HERO_ROTATION.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Show photo ${i + 1}`}
                    onClick={() => setIdx(i)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === idx ? 'w-6 bg-[#D4A843]' : 'w-1.5 bg-white/30 hover:bg-white/50'
                    }`}
                    data-testid={`home-hero-dot-${i}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Wallet card (copy + CTAs) ───────────────────────────────────── */}
        <div className="order-2 lg:order-1 relative">
          <div className="relative rounded-[26px] border border-white/10 bg-white/[0.03] overflow-hidden shadow-[0_22px_60px_rgba(0,0,0,0.7),inset_0_1px_0_rgba(255,255,255,0.05),0_0_0_1px_rgba(212,168,67,0.06)]">
            {/* Soft inner highlight */}
            <div
              aria-hidden="true"
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'radial-gradient(600px circle at 0% 0%, rgba(212,168,67,0.08), transparent 60%)',
              }}
            />
            <div className="relative p-5 sm:p-7 lg:p-9">
              {/* Pills row */}
              <div className="flex flex-wrap items-center gap-2 mb-5 sm:mb-6">
                <MembershipPill testId="home-hero-tonight-pill">TONIGHT · 10PM</MembershipPill>
                <MembershipPill testId="home-hero-age-pill" tone="muted">21+ ONLY</MembershipPill>
              </div>

              {/* Headline (copy preserved verbatim) */}
              <h1
                data-testid="home-hero-headline"
                className="font-['EB_Garamond'] text-[44px] sm:text-5xl lg:text-6xl font-semibold tracking-[-0.02em] leading-[0.95] text-white max-w-[16ch]"
              >
                <span className="block">WHERE</span>
                <span className="block">NIGHTS</span>
                <span className="block text-[#D4A843]">IGNITE</span>
              </h1>

              {/* Subhead + tagline */}
              <p
                data-testid="home-hero-subhead"
                className="mt-5 sm:mt-6 text-sm sm:text-base md:text-lg leading-relaxed text-white/75 max-w-[52ch]"
              >
                Underground sound. Premium spirits. No phones on the floor.
              </p>
              <p
                data-testid="home-hero-tagline"
                className="mt-2 text-sm sm:text-base text-[#F1E2B8] font-medium"
              >
                This is your escape.
              </p>

              {/* CTAs */}
              <div className="mt-7 sm:mt-8 grid grid-cols-1 sm:grid-cols-[auto_auto] gap-3 sm:gap-3">
                <Button
                  data-testid="home-hero-reserve-button"
                  onClick={() => navigate('/checkin')}
                  className="h-11 sm:h-12 px-6 rounded-xl bg-[#D4A843] hover:bg-[#E0B450] text-[#120F08] font-semibold tracking-tight focus-visible:ring-2 focus-visible:ring-[#D4A843]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A0A0A] transition-[background-color,box-shadow,transform] duration-200"
                  style={{ boxShadow: '0 8px 28px rgba(212,168,67,0.28)' }}
                >
                  Reserve Your Table
                  <ArrowRight size={16} className="ml-2" />
                </Button>
                <Button
                  data-testid="home-hero-view-week-button"
                  variant="outline"
                  onClick={() => navigate('/events')}
                  className="h-11 sm:h-12 px-6 rounded-xl border-white/15 bg-white/[0.02] text-white/85 hover:bg-white/[0.06] hover:border-white/25 transition-[background-color,border-color] duration-200"
                >
                  View This Week
                </Button>
              </div>

              {/* Bottom hairline + secondary affordances */}
              <div className="mt-7 sm:mt-8 pt-5 border-t border-white/10 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-white/60">
                <span className="inline-flex items-center gap-1.5">
                  <MapPin size={12} className="text-[#D4A843]" /> Sonari · Jamshedpur
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <CalendarIcon size={12} className="text-[#D4A843]" /> Fri – Sun · 6 PM onwards
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Sparkles size={12} className="text-[#D4A843]" /> Premium · Curated
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  2. EDITORIAL GALLERY (bento bento)
// ════════════════════════════════════════════════════════════════════════════
export function EditorialGallery() {
  const ref = useReveal();
  // Bento layout: 10 photos in an asymmetric 12-col grid with 2 hero tiles.
  // Each tile carries a Tailwind class snippet for its grid placement.
  const tiles = [
    { src: PHOTOS[0], className: 'md:col-span-7 md:row-span-2 aspect-[4/5] md:aspect-auto' },     // hero-a (tall left)
    { src: PHOTOS[1], className: 'md:col-span-5 aspect-[16/11]' },
    { src: PHOTOS[2], className: 'md:col-span-3 aspect-square' },
    { src: PHOTOS[3], className: 'md:col-span-2 aspect-square' },
    { src: PHOTOS[4], className: 'md:col-span-5 md:row-span-2 aspect-[4/5] md:aspect-auto' },     // hero-b (tall right)
    { src: PHOTOS[5], className: 'md:col-span-3 aspect-square' },
    { src: PHOTOS[6], className: 'md:col-span-4 aspect-square' },
    { src: PHOTOS[7], className: 'md:col-span-4 aspect-square' },
    { src: PHOTOS[8], className: 'md:col-span-4 aspect-square' },
    { src: PHOTOS[9], className: 'md:col-span-4 aspect-square' },
  ];

  return (
    <section
      ref={ref}
      data-testid="home-gallery"
      className="relative bg-[#0A0A0A] py-14 sm:py-20 lg:py-24"
    >
      <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-10">
        {/* Section eyebrow + title */}
        <div className="flex items-end justify-between gap-4 mb-8 sm:mb-10 flex-wrap">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#D4A843] mb-2 font-semibold">
              The Floor · Recent Nights
            </p>
            <h2 className="font-['EB_Garamond'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
              Moments. Off the record.
            </h2>
          </div>
          <div className="hidden sm:block text-[11px] text-white/40 tracking-[0.18em] uppercase max-w-[28ch] text-right">
            Captured by the room. <br />Shared with permission.
          </div>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-3 sm:gap-4 [grid-auto-rows:1fr]">
          {tiles.map((t, i) => (
            <figure
              key={t.src + i}
              data-testid={`home-gallery-image-${String(i + 1).padStart(2, '0')}`}
              className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-[#121216] ${t.className}`}
            >
              <img
                src={t.src}
                alt={`Scratch Unleashed nightlife moment ${i + 1}`}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover transition-transform duration-[650ms]"
                style={{ transitionTimingFunction: 'cubic-bezier(0.22,1,0.36,1)' }}
                onLoad={(e) => { e.currentTarget.classList.add('scale-100'); }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.04)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1.0)'; }}
              />
              <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/0 via-black/10 to-black/70"
              />
              {/* Border lift on hover */}
              <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none rounded-2xl ring-1 ring-transparent group-hover:ring-[rgba(212,168,67,0.35)] transition-[box-shadow] duration-200"
              />
            </figure>
          ))}
        </div>

        {/* Footer line */}
        <p className="mt-8 text-center text-[11px] tracking-[0.2em] uppercase text-white/40">
          No phones on the floor · House team only · 21+
        </p>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  3. PROMOTER TEASER
// ════════════════════════════════════════════════════════════════════════════
export function PromoterTeaser({ navigate }) {
  const ref = useReveal();
  return (
    <section
      ref={ref}
      data-testid="home-promoter-teaser"
      className="relative bg-[#0A0A0A] py-14 sm:py-20"
    >
      <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-10">
        <div className="relative overflow-hidden rounded-[26px] border border-white/10 bg-gradient-to-br from-[#0F0F12] via-[#0A0A0A] to-[#0F0F12]">
          {/* Decorative photo on the right */}
          <div
            aria-hidden="true"
            className="absolute inset-y-0 right-0 w-[55%] hidden md:block"
            style={{
              backgroundImage: `url(${PHOTOS[7]})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              maskImage:
                'linear-gradient(to right, transparent 0%, black 35%, black 100%)',
              WebkitMaskImage:
                'linear-gradient(to right, transparent 0%, black 35%, black 100%)',
              opacity: 0.55,
            }}
          />
          {/* Soft gold edge */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(700px circle at 0% 50%, rgba(212,168,67,0.10), transparent 55%)',
            }}
          />
          <div className="relative grid md:grid-cols-2 gap-6 lg:gap-10 p-6 sm:p-10 lg:p-14">
            <div>
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#D4A843] mb-3 font-semibold">
                Promoter Program · Invite Only
              </p>
              <h3 className="font-['EB_Garamond'] text-3xl sm:text-4xl font-semibold tracking-tight text-white">
                Bring the room.<br />
                <span className="text-[#D4A843]">Earn the night.</span>
              </h3>
              <p className="mt-4 text-sm sm:text-base text-white/75 max-w-[48ch]">
                A 3-digit code, a personal link, and a wallet that pays you on Sundays.
                Apply to join the invite-only program.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button
                  data-testid="home-promoter-cta"
                  onClick={() => navigate('/promoters')}
                  className="h-11 px-6 rounded-xl bg-[#D4A843] hover:bg-[#E0B450] text-[#120F08] font-semibold transition-[background-color] duration-200"
                >
                  <BadgeIndianRupee size={16} className="mr-2" />
                  Become a Promoter
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/promoters/apply')}
                  className="h-11 px-6 rounded-xl border-white/15 bg-white/[0.02] text-white/85 hover:bg-white/[0.06]"
                  data-testid="home-promoter-apply-cta"
                >
                  Have an invite?
                </Button>
              </div>
            </div>
            <div className="hidden md:block" /> {/* spacer column to balance grid */}
          </div>
        </div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  4. RESERVATION STRIP FOOTER
// ════════════════════════════════════════════════════════════════════════════
export function ReservationStripFooter({ navigate }) {
  const ref = useReveal();
  return (
    <section
      ref={ref}
      data-testid="home-footer-reserve-strip"
      className="relative bg-[#0A0A0A] pt-12 sm:pt-16 pb-10"
    >
      <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-10">
        {/* Top conversion strip */}
        <div className="relative overflow-hidden rounded-[26px] border border-white/10 bg-gradient-to-b from-white/[0.03] to-white/[0.01]">
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(900px circle at 80% 0%, rgba(212,168,67,0.12), transparent 50%)',
            }}
          />
          <div className="relative grid md:grid-cols-[1.2fr_0.8fr] gap-6 lg:gap-10 p-6 sm:p-10 lg:p-14 items-center">
            <div>
              <h3 className="font-['EB_Garamond'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
                Your night<br />
                <span className="text-[#D4A843]">awaits.</span>
              </h3>
              <p className="mt-4 text-sm sm:text-base text-white/70 max-w-[42ch]">
                Reserve a table, join the guest list, or just walk in for cover.
                We open the doors at 6 PM, Friday through Sunday.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate('/checkin')}
                data-testid="home-footer-reserve-cta"
                className="h-12 px-6 rounded-xl bg-[#D4A843] hover:bg-[#E0B450] text-[#120F08] font-semibold transition-[background-color] duration-200"
                style={{ boxShadow: '0 8px 28px rgba(212,168,67,0.28)' }}
              >
                Reserve Your Table
                <ArrowRight size={16} className="ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/events')}
                data-testid="home-footer-events-link"
                className="h-12 px-6 rounded-xl border-white/15 bg-white/[0.02] text-white/85 hover:bg-white/[0.06] hover:border-white/25 transition-[background-color,border-color] duration-200"
              >
                View This Week
              </Button>
            </div>
          </div>
        </div>

        {/* Utility row */}
        <div className="mt-8 grid sm:grid-cols-3 gap-3">
          <FooterLink
            icon={LogIn}
            label="Sign in"
            href="/account/login"
            navigate={navigate}
            testId="home-footer-login-link"
          />
          <FooterLink
            icon={ScanLine}
            label="Guest check-in"
            href="/checkin"
            navigate={navigate}
            testId="home-footer-checkin-link"
          />
          <FooterLink
            icon={BadgeIndianRupee}
            label="Promoter program"
            href="/promoters"
            navigate={navigate}
            testId="home-footer-promoters-link"
          />
        </div>
      </div>
    </section>
  );
}

function FooterLink({ icon: Icon, label, href, navigate, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => navigate(href)}
      className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20 px-4 py-3 text-sm text-white/85 transition-[background-color,border-color] duration-200 group"
    >
      <span className="inline-flex items-center gap-3">
        <span className="w-8 h-8 rounded-full bg-[#D4A843]/10 border border-[rgba(212,168,67,0.35)] flex items-center justify-center">
          <Icon size={14} className="text-[#D4A843]" />
        </span>
        {label}
      </span>
      <ArrowRight
        size={14}
        className="text-white/40 group-hover:text-[#D4A843] group-hover:translate-x-0.5 transition-[color,transform] duration-200"
      />
    </button>
  );
}
